import { UnionToIntersection, ValueOf } from 'ts-utils';
import { Experiment } from '../../helpers/experiments';
import { AllExperiments } from './AllExperiments';
import { VariantOf, VariantsConfig } from './Experiment';
import { QueryParamExperiment } from './QueryParamExperiment';

const KEY = 'ExperimentOverrides';

type ExperimentName = Extract<keyof typeof AllExperiments, string>;
type Variants = (typeof AllExperiments)[ExperimentName]['variants'];
type Variant = VariantOf<Variants>;

type ExperimentValues = ValueOf<typeof AllExperiments>;
// actual variant values
export type ExpVariant = ValueOf<UnionToIntersection<ExperimentValues['variants']>>;

export type ExperimentStorageOverride = { [expName: string]: string };

/**
 * Apply overrides from local storage. Used by integration tests.
 */
function load(): void {
  const overrides: ExperimentStorageOverride = loadFromStorage();
  Object.entries(overrides).forEach(([expName, expVariantName]) => {
    // assume expName is the name of an experiment, and expVariantName is the name of a variant
    AllExperiments[expName as ExperimentName]?.overrideVariant(expVariantName as Variant);
  });
}

function loadFromStorage(): ExperimentStorageOverride {
  const json = localStorage.getItem(KEY) ?? '{}';
  return JSON.parse(json);
}

function storeAll(variants: ExperimentStorageOverride): void {
  localStorage.setItem('ExperimentOverrides', JSON.stringify(variants));
}

function storeSingle<VC extends VariantsConfig>(
  experiment: Experiment<VC>,
  variant: VariantOf<VC>
): void {
  const existingStoredVariation = loadFromStorage();
  const storedVariation = { [experiment.name]: variant };
  const newStoredOverride = {
    ...existingStoredVariation,
    ...storedVariation,
  } as ExperimentStorageOverride;
  storeAll(newStoredOverride);
}

/** Update an experiments value in the query param to keep in sync after
 * changing it in code (like the dev panel does) */
function updateQueryParam(
  experiment: QueryParamExperiment<VariantsConfig>,
  newVariant: VariantOf<VariantsConfig>
): void {
  const [queryValue] = Object.entries(experiment.paramToVariantMapping)
    .filter(([, value]) => value === newVariant)
    .map(([key]) => key);

  if (queryValue !== undefined) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(experiment.queryParam, queryValue);
    window.history.replaceState(undefined, '', `${window.location.pathname}?${searchParams}`);
  }
}

export const ExperimentStorage = {
  load,
  storeAll,
  storeSingle,
  updateQueryParam,
};
